import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { assessmentRulesAPI, studentEnumsAPI } from '../lib/api';
import { ClipboardList, Plus, Pencil, Trash2 } from 'lucide-react';

/**
 * AssessmentRules — admin page.
 * Lets the admin build a mapping of {year_of_study + course_type + course_length}
 * → number of assessments per placement + optional interval override.
 * Placement creation resolves the most specific matching rule; falls back to the
 * global Settings default when no rule matches.
 */
const AssessmentRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    year_of_study: '',
    course_type: '',
    course_length: '',
    assessments_per_placement: 4,
    interval_days: '',
    notes: '',
  });
  const [enums, setEnums] = useState({ years: [], course_types: [], course_lengths: [] });

  useEffect(() => {
    reload();
    studentEnumsAPI.list().then((r) => setEnums(r.data || {})).catch(() => {});
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await assessmentRulesAPI.list();
      setRules(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ year_of_study: '', course_type: '', course_length: '', assessments_per_placement: 4, interval_days: '', notes: '' });
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      year_of_study: rule.year_of_study || '',
      course_type: rule.course_type || '',
      course_length: rule.course_length || '',
      assessments_per_placement: rule.assessments_per_placement ?? 4,
      interval_days: rule.interval_days ?? '',
      notes: rule.notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      year_of_study: form.year_of_study || null,
      course_type: form.course_type || null,
      course_length: form.course_length || null,
      assessments_per_placement: Number(form.assessments_per_placement) || 0,
      interval_days: form.interval_days === '' ? null : Number(form.interval_days),
      notes: form.notes || '',
    };
    try {
      if (editing) {
        await assessmentRulesAPI.update(editing.id, payload);
        toast.success('Rule updated');
      } else {
        await assessmentRulesAPI.create(payload);
        toast.success('Rule created');
      }
      setModalOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete this rule? Any future placements matching this combination will fall back to the default.`)) return;
    try {
      await assessmentRulesAPI.delete(rule.id);
      toast.success('Rule deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const renderScope = (rule) => {
    const parts = [];
    if (rule.year_of_study) parts.push(rule.year_of_study);
    if (rule.course_type) parts.push(rule.course_type);
    if (rule.course_length) parts.push(rule.course_length);
    if (parts.length === 0) return <em className="text-gray-500">Any student (catch-all)</em>;
    return parts.join(' · ');
  };

  return (
    <Layout>
      <div data-testid="assessment-rules-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Assessment Rules</h1>
            <p className="text-gray-500 mt-1">
              Determine how many feedback assessments a placement gets, based on the student's year and course.
              The most specific rule wins; if no rule matches, the global default in <strong>Settings</strong> is used.
            </p>
          </div>
          <Button data-testid="new-rule-btn" onClick={openCreate} >
            <Plus className="w-4 h-4 mr-1" /> New Rule
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <ClipboardList className="w-5 h-5" /> Rules
            </CardTitle>
            <CardDescription>
              Leave a field blank to match ANY value for that field. Feedback template is shared for now — will
              be per-rule in a future update.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading…</div>
            ) : rules.length === 0 ? (
              <div className="py-10 text-center text-gray-500 italic">
                No rules yet. All placements use the global default from Settings ({ /* hint only */ } 4 assessments · 30 day interval).
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2 px-3">Scope</th>
                    <th className="text-right py-2 px-3"># Assessments</th>
                    <th className="text-right py-2 px-3">Interval (days)</th>
                    <th className="text-left py-2 px-3">Notes</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100" data-testid={`rule-row-${r.id}`}>
                      <td className="py-3 px-3 text-gray-800">{renderScope(r)}</td>
                      <td className="py-3 px-3 text-right font-mono font-semibold text-ica-navy">{r.assessments_per_placement}</td>
                      <td className="py-3 px-3 text-right text-gray-700">{r.interval_days ?? <em className="text-gray-400">default</em>}</td>
                      <td className="py-3 px-3 text-xs text-gray-500 max-w-[280px] truncate">{r.notes || '—'}</td>
                      <td className="py-3 px-3 text-right">
                        <Button data-testid={`edit-rule-${r.id}`} size="sm" variant="ghost" onClick={() => openEdit(r)} className="mr-1">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`delete-rule-${r.id}`} size="sm" variant="ghost" onClick={() => handleDelete(r)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-lg" data-testid="rule-modal">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Rule' : 'New Rule'}</DialogTitle>
              <DialogDescription>
                Blank fields mean “applies to any value”. The most specific rule wins if two rules could match a student.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Year of study</Label>
                <Select value={form.year_of_study} onValueChange={(v) => setForm(f => ({ ...f, year_of_study: v === '__any__' ? '' : v }))}>
                  <SelectTrigger data-testid="rule-year"><SelectValue placeholder="Any year" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any year</SelectItem>
                    {(enums.years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course type</Label>
                <Select value={form.course_type} onValueChange={(v) => setForm(f => ({ ...f, course_type: v === '__any__' ? '' : v }))}>
                  <SelectTrigger data-testid="rule-course-type"><SelectValue placeholder="Any course type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any course type</SelectItem>
                    {(enums.course_types || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course length</Label>
                <Select value={form.course_length} onValueChange={(v) => setForm(f => ({ ...f, course_length: v === '__any__' ? '' : v }))}>
                  <SelectTrigger data-testid="rule-course-length"><SelectValue placeholder="Any course length" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any course length</SelectItem>
                    {(enums.course_lengths || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label># Assessments</Label>
                  <Input
                    data-testid="rule-count"
                    type="number"
                    min={0}
                    value={form.assessments_per_placement}
                    onChange={(e) => setForm(f => ({ ...f, assessments_per_placement: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Default interval (initial dates only)</Label>
                  <Input
                    data-testid="rule-interval"
                    type="number"
                    min={1}
                    placeholder="Use default"
                    value={form.interval_days}
                    onChange={(e) => setForm(f => ({ ...f, interval_days: e.target.value }))}
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Only used to pre-fill each assessment&apos;s due date at placement creation. Admin can override each date via the date picker.
                  </p>
                </div>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  data-testid="rule-notes"
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Diploma students get more assessments per term"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="button" data-testid="save-rule-btn" onClick={handleSave} >
                {editing ? 'Save changes' : 'Create rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AssessmentRules;
